import { useEffect, useMemo, useState } from "react";
import {
  listMyAdminTrails,
  ApiError,
  type AdminAssignment,
  type EmbeddedOrganization,
  type OrganizationProfile,
} from "../api";
import { AdminAddTrail } from "./AdminAddTrail";
import { OrgDrawer } from "./OrgDrawer";
import { OrgAnalyticsView } from "./OrgAnalyticsView";
import { AdminTrailNotifications } from "./AdminTrailNotifications";
import { AdminTrailDetails } from "./AdminTrailDetails";
import { EventsSection } from "./EventsSection";
import { WebsiteEmbedTool } from "./WebsiteEmbedTool";
import { GpxUploadTool } from "./GpxUploadTool";
import { DashboardShell, type SidebarGroup } from "./DashboardShell";
import { SetStatusPill } from "./SetStatusPill";

type ToolId =
  | "notifications"
  | "events"
  | "details"
  | "website-embed"
  | "gpx"
  | "add-trail";

const TOOL_LABEL: Record<ToolId, string> = {
  notifications: "Notifications",
  events: "Events",
  details: "Trail details",
  "website-embed": "Website embed",
  gpx: "GPX",
  "add-trail": "Add a trail",
};

const TOOL_SUBTITLE: Record<ToolId, string> = {
  notifications: "Push the current trail condition to the riders who follow this trail.",
  events: "Trail days, work parties, group rides — anything you want on the calendar.",
  details: "Location, amenities, and crew for this trail.",
  "website-embed":
    "Drop a live status badge into your trail club's website. " +
    "Updates the instant you push from Notifications — no cron lag.",
  gpx:
    "Upload route files. Riders see colored polylines on the trail map and a " +
    "scrub-able elevation profile — RideWithGPS / Komoot vibes, on your trail.",
  "add-trail": "Claim an existing trail in the catalog or propose a new one.",
};

/**
 * Admin workspace — OLAP-style dashboard for trail administration.
 *
 * Sidebar sections are the "tools" the admin works with: Notifications
 * (status push), Events (calendar), Trail details (metadata + crew).
 * The trail picker lives at the top of the content area since each
 * tool operates on the currently-selected trail; if an admin has
 * multiple trails (often across multiple orgs) the picker groups them
 * by org for clean wayfinding.
 *
 * "Add a trail" is its own sidebar tool — once-per-relationship action
 * that deserves a dedicated surface instead of competing for attention
 * with the daily-driver flows.
 */
export function AdminWorkspace() {
  const [trails, setTrails] = useState<AdminAssignment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  /** When set, the admin is viewing the per-org analytics dashboard.
   *  Renders full-page (replaces the shell entirely) so analytics gets
   *  its own focused surface. */
  const [analyticsForOrgId, setAnalyticsForOrgId] = useState<string | null>(null);

  const [activeTool, setActiveTool] = useState<ToolId>("notifications");
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTrails(null);
    setError(null);
    listMyAdminTrails()
      .then((r) => {
        if (cancelled) return;
        setTrails(r.items);
        // Default the picker to the first trail on initial load so the
        // user lands on a populated Notifications tool. Once the user
        // has picked one explicitly, preserve their choice across
        // reloads when possible.
        setActiveSlug((cur) => {
          if (cur && r.items.some((t) => t.slug === cur)) return cur;
          return r.items[0]?.slug ?? null;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Couldn't load your trails."
        );
        setTrails([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function reload() {
    setReloadKey((k) => k + 1);
  }

  function applyOrgUpdate(updated: OrganizationProfile) {
    setTrails((cur) => {
      if (!cur) return cur;
      return cur.map((t) =>
        t.organization && t.organization.id === updated.id
          ? {
              ...t,
              organization: {
                id: updated.id,
                slug: updated.slug,
                name: updated.name,
                logoUrl: updated.logoUrl,
              },
            }
          : t
      );
    });
  }

  const groups: SidebarGroup[] = useMemo(
    () => [
      {
        id: "tools",
        label: "Tools",
        items: [
          { id: "notifications", label: TOOL_LABEL.notifications },
          { id: "events", label: TOOL_LABEL.events },
          { id: "details", label: TOOL_LABEL.details },
          { id: "website-embed", label: TOOL_LABEL["website-embed"] },
          { id: "gpx", label: TOOL_LABEL.gpx },
        ],
      },
      {
        id: "catalog",
        label: "Catalog",
        items: [{ id: "add-trail", label: TOOL_LABEL["add-trail"] }],
      },
    ],
    []
  );

  if (analyticsForOrgId) {
    return (
      <OrgAnalyticsView
        orgId={analyticsForOrgId}
        onBack={() => setAnalyticsForOrgId(null)}
        backLabel="Back to your trails"
      />
    );
  }

  const activeTrail = trails?.find((t) => t.slug === activeSlug) ?? null;
  const siblings = activeTrail
    ? (trails ?? []).filter(
        (t) =>
          t.slug !== activeTrail.slug &&
          t.statusPageUrl === activeTrail.statusPageUrl
      )
    : [];

  // Build the page title + subtitle from the active tool + active trail.
  // For per-trail tools, lead with the trail name; for Add a trail,
  // skip the prefix.
  let pageTitle: string;
  if (activeTool === "add-trail") {
    pageTitle = TOOL_LABEL["add-trail"];
  } else if (activeTrail) {
    pageTitle = `${activeTrail.name} · ${TOOL_LABEL[activeTool]}`;
  } else {
    pageTitle = TOOL_LABEL[activeTool];
  }

  return (
    <>
      <DashboardShell
        groups={groups}
        activeId={activeTool}
        onSelect={(id) => setActiveTool(id as ToolId)}
        breadcrumb={["My Trails", TOOL_LABEL[activeTool]]}
        pageTitle={pageTitle}
        pageSubtitle={TOOL_SUBTITLE[activeTool]}
        contextBadge={activeTrail ? roleLabel(activeTrail.role) : undefined}
        quickAction={<SetStatusPill />}
      >
        {error && <div className="error">{error}</div>}

        {/* Trail picker — appears for per-trail tools only. Hidden on
            Add a trail since that flow doesn't operate on a specific
            trail. */}
        {activeTool !== "add-trail" && (
          <TrailPicker
            trails={trails}
            activeSlug={activeSlug}
            onPick={setActiveSlug}
            onEditOrg={(id) => setEditingOrgId(id)}
            onOpenAnalytics={(id) => setAnalyticsForOrgId(id)}
          />
        )}

        {activeTool === "add-trail" ? (
          <AdminAddTrail onChanged={reload} />
        ) : trails === null ? (
          <div className="muted">Hitting the trail…</div>
        ) : trails.length === 0 ? (
          <EmptyAdminState onAddTrail={() => setActiveTool("add-trail")} />
        ) : !activeTrail ? (
          <div className="muted">Pick a trail above to get started.</div>
        ) : activeTool === "notifications" ? (
          <AdminTrailNotifications
            trail={activeTrail}
            siblings={siblings}
            onChanged={reload}
          />
        ) : activeTool === "events" ? (
          <EventsSection
            slug={activeTrail.slug}
            canHost={
              activeTrail.role === "owner" || activeTrail.role === "steward"
            }
          />
        ) : activeTool === "website-embed" ? (
          <WebsiteEmbedTool trail={activeTrail} />
        ) : activeTool === "gpx" ? (
          <GpxUploadTool trail={activeTrail} />
        ) : (
          <AdminTrailDetails trail={activeTrail} />
        )}
      </DashboardShell>

      {editingOrgId && (
        <OrgDrawer
          orgId={editingOrgId}
          onClose={() => setEditingOrgId(null)}
          onSaved={applyOrgUpdate}
        />
      )}
    </>
  );
}

function roleLabel(role: "owner" | "steward" | "member"): string {
  if (role === "owner") return "Trail Boss";
  if (role === "steward") return "Crew";
  return "Rider";
}

function EmptyAdminState({ onAddTrail }: { onAddTrail: () => void }) {
  return (
    <div className="card" style={{ textAlign: "center", padding: 40 }}>
      <div className="empty">
        No trails assigned to you yet.
      </div>
      <button
        type="button"
        onClick={onAddTrail}
        style={{ marginTop: 16 }}
      >
        Add a trail
      </button>
    </div>
  );
}

interface OrgBucket {
  org: EmbeddedOrganization | null;
  trails: AdminAssignment[];
}

function groupByOrg(trails: AdminAssignment[]): OrgBucket[] {
  const buckets = new Map<string, OrgBucket>();
  const order: string[] = [];
  for (const t of trails) {
    const key = t.organization?.id ?? "__unparented__";
    if (!buckets.has(key)) {
      buckets.set(key, { org: t.organization, trails: [] });
      order.push(key);
    }
    buckets.get(key)!.trails.push(t);
  }
  return order.map((k) => buckets.get(k)!);
}

/**
 * Sticky trail-picker row at the top of the content area. Groups
 * trails by their parent org so an admin with trails across multiple
 * orgs can find the right one quickly. Each org row also surfaces
 * Edit org + Analytics buttons — those used to live on the OrgCard
 * header in the old workspace and need a home in the dashboard model.
 */
function TrailPicker({
  trails,
  activeSlug,
  onPick,
  onEditOrg,
  onOpenAnalytics,
}: {
  trails: AdminAssignment[] | null;
  activeSlug: string | null;
  onPick: (slug: string) => void;
  onEditOrg: (orgId: string) => void;
  onOpenAnalytics: (orgId: string) => void;
}) {
  if (!trails || trails.length === 0) return null;
  const buckets = groupByOrg(trails);

  return (
    <div className="admin-trail-picker">
      {buckets.map((bucket) => {
        const canEdit = bucket.trails.some((t) => t.role === "owner");
        return (
          <div key={bucket.org?.id ?? "unparented"} className="admin-trail-picker-group">
            <div className="admin-trail-picker-org">
              <div className="admin-trail-picker-org-id">
                {bucket.org?.logoUrl ? (
                  <img
                    className="admin-trail-picker-org-logo"
                    src={bucket.org.logoUrl}
                    alt=""
                  />
                ) : (
                  <span className="admin-trail-picker-org-logo placeholder">
                    🏔
                  </span>
                )}
                <span className="admin-trail-picker-org-name">
                  {bucket.org?.name ?? "Unparented trails"}
                </span>
              </div>
              {bucket.org && (
                <div className="row" style={{ gap: 6 }}>
                  <button
                    type="button"
                    className="small ghost"
                    onClick={() => onOpenAnalytics(bucket.org!.id)}
                    title="Open analytics for this org"
                  >
                    📊 Analytics
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      className="small secondary"
                      onClick={() => onEditOrg(bucket.org!.id)}
                    >
                      Edit org
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="admin-trail-picker-row">
              {bucket.trails.map((t) => (
                <button
                  key={t.slug}
                  type="button"
                  className={`admin-trail-pill ${t.slug === activeSlug ? "is-active" : ""}`}
                  onClick={() => onPick(t.slug)}
                  aria-pressed={t.slug === activeSlug}
                >
                  <span className="admin-trail-pill-name">{t.name}</span>
                  {(t.region || t.state) && (
                    <span className="admin-trail-pill-sub">
                      {[t.region, t.state].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
